/**
 * "Google-style" recenter animation for the web map: zoom out → pan → zoom in.
 * Mirrors the mobile app's _animateGoogleStyleRecenter (distance-based pull-back
 * zoom and phase timing) using the Google Maps JS camera, which animates each
 * setZoom/panTo step natively. We sequence the steps by waiting for the map to
 * settle (idle) after each, with a duration-based fallback.
 */

export interface LatLngLiteral {
  lat: number
  lng: number
}

function haversineMeters(a: LatLngLiteral, b: LatLngLiteral): number {
  const R = 6371000
  const lat1 = (a.lat * Math.PI) / 180
  const lat2 = (b.lat * Math.PI) / 180
  const dLat = lat2 - lat1
  const dLng = ((b.lng - a.lng) * Math.PI) / 180
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(x))
}

/** Pull-back (zoomed-out) level by distance — mirrors the mobile thresholds. */
function pullbackZoom(distanceMeters: number, finalZoom: number, currentZoom: number): number {
  if (distanceMeters > 3000) return 12.8
  if (distanceMeters > 800) return 13.6
  if (distanceMeters > 200) return 14.6
  return Math.max(finalZoom - 0.4, currentZoom - 1.6)
}

/** Total animation time by distance (sqrt-eased 1200–3400ms; 650ms when close). */
function totalDurationMs(distanceMeters: number): number {
  if (distanceMeters < 80) return 650
  const t = Math.min(1, Math.max(0, (distanceMeters - 80) / 3200))
  const eased = Math.sqrt(t)
  return Math.round(1200 + eased * (3400 - 1200))
}

/** Resolves on the next map 'idle', or after fallbackMs — whichever comes first. */
function waitForSettle(map: any, fallbackMs: number): Promise<void> {
  return new Promise(resolve => {
    let done = false
    let listener: any = null
    const finish = () => {
      if (done) return
      done = true
      clearTimeout(timer)
      if (listener) listener.remove()
      resolve()
    }
    listener = window.google.maps.event.addListenerOnce(map, 'idle', finish)
    const timer = setTimeout(finish, fallbackMs)
  })
}

/**
 * Animate the map to `target` with a zoom-out → pan → zoom-in pull-back.
 * Fire-and-forget friendly (returns a promise you can ignore).
 */
export async function flyTo(map: any, target: LatLngLiteral, finalZoom = 16): Promise<void> {
  const center = map.getCenter?.()
  if (!center) {
    map.setCenter(target)
    map.setZoom(finalZoom)
    return
  }

  const start = { lat: center.lat(), lng: center.lng() }
  const distance = haversineMeters(start, target)
  const currentZoom = map.getZoom() ?? finalZoom

  const total = totalDurationMs(distance)
  const zoomOutMs = Math.max(300, Math.round(total * 0.28))
  const panMs = Math.max(340, Math.round(total * 0.44))
  const zoomInMs = Math.max(300, Math.round(total * 0.28))

  // 1) Zoom out in place (only when it's actually a zoom-out).
  const pullback = pullbackZoom(distance, finalZoom, currentZoom)
  if (pullback < currentZoom - 0.1) {
    map.setZoom(pullback)
    await waitForSettle(map, zoomOutMs)
  }

  // 2) Pan to the target at the pulled-back zoom (animates because it's on-screen).
  map.panTo(target)
  await waitForSettle(map, panMs)

  // 3) Zoom in, centered on the target.
  map.setZoom(finalZoom)
  await waitForSettle(map, zoomInMs)
}
